import { IconButton, Tooltip, Typography } from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { BookingType } from "../../../../../../../features/Booking-Form/typings/booking-inputs";
import { useRemoteFetchBookingFavoritesByFloor } from "../../../../../../../hooks/Remote/BookingFavorites/useRemoteFetchBookingFavoritesByFloor";
import { useRemoteAddBookingFavorite } from "../../../../../../../hooks/Remote/BookingFavorites/useRemoteAddBookingFavorite";
import { useRemoteRemoveBookingFavorite } from "../../../../../../../hooks/Remote/BookingFavorites/useRemoteRemoveBookingFavorite";
import { isBookingTypeWithFavorites } from "../../../../../../../hooks/Remote/BookingFavorites/is-booking-type-with-favorites";

/**
 * Star for one bookable object on the current floor.
 * Used by the book prompt and the colleague-floor room sheet.
 */
type BookingObjectFavoriteButtonProps = {
  /** Workplace, parking, charging, or conference. Other types hide the star. */
  bookingType?: BookingType | null;
  /** Inventory the star belongs to. Zero or missing hides the star. */
  bookingInventoryId: number;
  /** Floor whose favorite list is read and updated. */
  floorInventoryId?: number;
};

export function BookingObjectFavoriteButton({
  bookingType,
  bookingInventoryId,
  floorInventoryId
}: Readonly<BookingObjectFavoriteButtonProps>) {
  const { t } = useTranslation();
  const showFavoriteControl =
    isBookingTypeWithFavorites(bookingType) && floorInventoryId != null && bookingInventoryId > 0;
  const { data: floorFavoritesData } = useRemoteFetchBookingFavoritesByFloor(
    showFavoriteControl ? floorInventoryId : null
  );
  const addFavorite = useRemoteAddBookingFavorite();
  const removeFavorite = useRemoteRemoveBookingFavorite();
  const isFavorite =
    showFavoriteControl &&
    bookingType != null &&
    (floorFavoritesData?.favorites.some(
      favorite =>
        favorite.bookingInventoryId === bookingInventoryId && favorite.bookingType === bookingType
    ) ??
      false);
  const favoriteBusy = addFavorite.isLoading || removeFavorite.isLoading;

  if (!showFavoriteControl || bookingType == null || floorInventoryId == null) {
    return null;
  }

  const toggleFavorite = async () => {
    const payload = {
      bookingType,
      bookingInventoryId,
      floorInventoryId
    };
    if (isFavorite) {
      await removeFavorite.mutateAsyncSafe(payload);
    } else {
      await addFavorite.mutateAsyncSafe(payload);
    }
  };

  return (
    <Tooltip
      title={
        <Typography>
          {isFavorite
            ? t("_bookingFavorites_removeFromFavorites")
            : t("_bookingFavorites_addToFavorites")}
        </Typography>
      }
    >
      <IconButton
        data-testid="favorite-booking-object-btn"
        disabled={favoriteBusy}
        onClick={() => {
          toggleFavorite().catch(() => undefined);
        }}
      >
        {isFavorite ? <Star color="warning" /> : <StarBorder />}
      </IconButton>
    </Tooltip>
  );
}
